import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect as useIoniconsEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { PageTitleContext } from '../contexts/PageTitleContext';
import UserImpersonationModal from './UserImpersonationModal';
import CursorTooltip from './CursorTooltip';
import NotificationBell from './NotificationBell';
import BugReportButton from './BugReportButton';
import { Button, Alert, Skeleton } from '@mui/material';
import { 
  AdminPanelSettings, 
  ExitToApp,
  Home,
  BarChart,
  List,
  CalendarToday,
  Description,
  Checklist,
  CheckCircle,
  Search,
  Star,
  StarBorder,
  Science,
  Build,
  LibraryBooks,
  Business,
  Settings,
  Logout,
  Person,
  ExpandMore,
  ExpandLess,
  ChevronLeft,
  ChevronRight,
  Menu,
  TrendingUp,
  Map,
  Water,
  Shield,
  Notifications,
  BugReport,
  People,
  Construction,
  Nature,
  Analytics,
  Delete,
  Receipt,
  CreditCard,
  DirectionsCar,
  LocationOn,
  Folder,
  Speed,
  ColorLens,
  Refresh,
  Mail,
  Schedule,
  GridOn,
  Brightness4,
  Brightness7,
  AdminPanelSettings as AdminIcon
} from '@mui/icons-material';
import { mdiSprinklerVariant } from '@mdi/js';
import { menuStructure } from '../config/menuStructure';
import { rememberLastListPage } from '../utils/lastListPage';
import {
  readFavoritePages,
  writeFavoritePages,
  readSidebarOpenPreference,
  writeSidebarOpenPreference
} from '../utils/favoritePages';
import { filterNavigationMenu } from '../utils/filterNavigationMenu';
import { createMdiMenuIcon } from './MdiMenuIcon';

const SprinklerVariantIcon = createMdiMenuIcon(mdiSprinklerVariant);

/** Survives Layout unmount so an empty-modules user is not refreshed forever. */
const moduleRefreshAttemptedByUserId = new Set();

/** Theme / compact-datagrid toggle sizing shared by sidebar + header. */
const CompactDatagridToggle = ({ size = 'sidebar', isMobile = false, label = '' }) => {
  const { theme, compactDatagrid, toggleCompactDatagrid } = useTheme();
  const isSidebar = size === 'sidebar';
  const width = isSidebar ? '60px' : (isMobile ? '48px' : '64px');
  const height = isSidebar ? '28px' : (isMobile ? '26px' : '36px');
  const knob = isSidebar ? 20 : (isMobile ? 20 : 30);
  const knobLeftOn = isSidebar ? '32px' : (isMobile ? '24px' : '34px');
  const knobLeftOff = isSidebar ? '4px' : (isMobile ? '4px' : '6px');
  const knobBottom = isSidebar ? '4px' : '3px';
  const iconSize = isSidebar ? '14px' : (isMobile ? '18px' : '22px');

  return (
    <CursorTooltip title={compactDatagrid ? 'Compact datagrid rows (on)' : 'Compact datagrid rows'}>
      <label
        style={{
          position: 'relative',
          display: label ? 'flex' : 'inline-block',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: label ? '8px' : 0,
          width: label ? '100%' : width,
          height: label ? 'auto' : height,
          flexShrink: 0,
          cursor: 'pointer',
        }}
      >
        {label ? (
          <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-text)', textAlign: 'left' }}>
            {label}
          </span>
        ) : null}
        <span
          style={{
            position: 'relative',
            display: 'inline-block',
            width,
            height,
            flexShrink: 0,
          }}
        >
        <input
          type="checkbox"
          checked={compactDatagrid}
          onChange={toggleCompactDatagrid}
          aria-label="Compact datagrid rows"
          style={{ opacity: 0, width: 0, height: 0 }}
        />
        <span
          style={{
            position: 'absolute',
            cursor: 'pointer',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: compactDatagrid
              ? (theme === 'light'
                ? 'linear-gradient(135deg, #fff, #e0e0e0)'
                : 'linear-gradient(135deg, var(--color-accent), var(--color-accent2))')
              : (theme === 'light'
                ? 'linear-gradient(135deg, #e8e8e8, #d0d0d0)'
                : 'linear-gradient(135deg, rgba(61, 99, 141, 0.45), rgba(61, 99, 141, 0.7))'),
            borderRadius: height,
            transition: 'background 0.4s',
            border: theme === 'light' ? '1px solid var(--color-border)' : 'none',
          }}
        />
        <span
          style={{
            position: 'absolute',
            height: `${knob}px`,
            width: `${knob}px`,
            left: compactDatagrid ? knobLeftOn : knobLeftOff,
            bottom: knobBottom,
            backgroundColor: theme === 'light' ? '#fff' : 'var(--color-text)',
            borderRadius: '50%',
            transition: 'left 0.4s',
            boxShadow: theme === 'light'
              ? '0 2px 6px rgba(0,0,0,0.10)'
              : '0 1px 3px rgba(0,0,0,0.2)',
            border: theme === 'light' ? '1px solid var(--color-border)' : 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: iconSize,
            color: theme === 'light' ? 'var(--color-accent)' : 'var(--color-accent2)',
            padding: 0,
          }}
        >
          <ion-icon
            className="sidebar-toggle-icon"
            name={compactDatagrid ? 'reorder-two-outline' : 'reorder-four-outline'}
            style={{
              fontSize: iconSize,
              color: theme === 'light' ? 'var(--color-accent)' : 'var(--color-accent2)',
              '--color': theme === 'light' ? 'var(--color-accent)' : 'var(--color-accent2)',
              display: 'inline-block',
              visibility: 'visible',
              opacity: '1',
            }}
          ></ion-icon>
        </span>
        </span>
      </label>
    </CursorTooltip>
  );
};

const collectMatchingMenuItems = (items, query, ancestors = []) => {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return [];
  const out = [];
  for (const item of items || []) {
    const label = String(item.label || '');
    const labelMatch = label.toLowerCase().includes(q);
    if (item.type === 'section' && item.children?.length) {
      if (labelMatch) {
        const walkLeaves = (nodes, path) => {
          for (const node of nodes) {
            if (node.type === 'section' && node.children?.length) {
              walkLeaves(node.children, [...path, node.label]);
            } else if (node.path) {
              out.push({ ...node, ancestorLabel: path[path.length - 1] || label });
            }
          }
        };
        walkLeaves(item.children, [...ancestors, label]);
      } else {
        out.push(...collectMatchingMenuItems(item.children, query, [...ancestors, label]));
      }
    } else if (item.path && labelMatch) {
      out.push({ ...item, ancestorLabel: ancestors[ancestors.length - 1] });
    }
  }
  return out;
};

const findMenuItemById = (items, id) => {
  for (const item of items || []) {
    if (item.id === id) return item;
    if (item.children) {
      const found = findMenuItemById(item.children, id);
      if (found) return found;
    }
  }
  return null;
};

const Layout = ({ children }) => {
  const { user, loading, permissionsReady, logout, impersonateUser, stopImpersonation, refreshUserPermissions } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pageTitle, setPageTitleState] = useState('');
  const setPageTitle = useCallback((title) => setPageTitleState(title || ''), []);
  const pageTitleContextValue = useMemo(() => ({ setPageTitle }), [setPageTitle]);
  const [favoritePages, setFavoritePages] = useState(() => readFavoritePages());
  const [expandedSections, setExpandedSections] = useState(new Set());
  const [showImpersonationModal, setShowImpersonationModal] = useState(false);
  const [impersonationError, setImpersonationError] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [menuSearch, setMenuSearch] = useState('');
  const searchInputRef = useRef(null);
  const moduleRefreshAttemptedRef = useRef(moduleRefreshAttemptedByUserId);
  const viewportModeRef = useRef(null);
  const lastSidebarTouchMoveAtRef = useRef(0);

  // Align with CSS: mobile ≤768, tablet 769–1024, desktop ≥1025.
  // Use matchMedia so JS and CSS cannot disagree; ignore mobile browser
  // chrome resize noise that stays within mobile/tablet (address bar).
  useEffect(() => {
    const mqMobile = window.matchMedia('(max-width: 768px)');
    const mqDesktop = window.matchMedia('(min-width: 1025px)');

    const getViewportMode = () => {
      if (mqDesktop.matches) return 'desktop';
      if (mqMobile.matches) return 'mobile';
      return 'tablet';
    };

    const checkDevice = () => {
      const nextViewportMode = getViewportMode();
      setIsMobile(nextViewportMode === 'mobile');
      setIsTablet(nextViewportMode === 'tablet');

      const previousViewportMode = viewportModeRef.current;
      const isInitialMeasurement = previousViewportMode == null;
      const isCrossingDesktopBoundary =
        !isInitialMeasurement &&
        ((previousViewportMode === 'desktop' && nextViewportMode !== 'desktop') ||
          (previousViewportMode !== 'desktop' && nextViewportMode === 'desktop'));

      if (isInitialMeasurement) {
        // Initial page load: remembered choice on desktop, closed on mobile/tablet overlay.
        const remembered = readSidebarOpenPreference();
        setSidebarOpen(nextViewportMode === 'desktop' ? (remembered == null ? true : remembered) : false);
      } else if (isCrossingDesktopBoundary) {
        // Preserve state within mobile/tablet; restore the desktop choice when docking again.
        const remembered = readSidebarOpenPreference();
        setSidebarOpen(nextViewportMode === 'desktop' ? (remembered == null ? true : remembered) : false);
      }

      viewportModeRef.current = nextViewportMode;
    };

    checkDevice();
    const onChange = () => checkDevice();
    mqMobile.addEventListener('change', onChange);
    mqDesktop.addEventListener('change', onChange);
    window.addEventListener('resize', checkDevice);
    return () => {
      mqMobile.removeEventListener('change', onChange);
      mqDesktop.removeEventListener('change', onChange);
      window.removeEventListener('resize', checkDevice);
    };
  }, []);

  // Phones use viewport-fit=cover, so the layout viewport runs under the
  // status bar. Lock document scroll while this shell is mounted; the CSS
  // lock only applies at tablet widths and below.
  useEffect(() => {
    document.documentElement.classList.add('app-shell-lock');
    return () => {
      document.documentElement.classList.remove('app-shell-lock');
    };
  }, []);

  // Ensure ionicons are properly styled when theme changes
  useEffect(() => {
    setTimeout(() => {
      const ionIcons = document.querySelectorAll('ion-icon[name="sunny-outline"], ion-icon[name="moon-outline"]');
      ionIcons.forEach(icon => {
        const color = theme === 'light' ? 'var(--color-accent)' : 'var(--color-accent2)';
        icon.style.setProperty('--color', color);
        icon.style.color = color;
        icon.style.display = 'inline-block';
        icon.style.visibility = 'visible';
        icon.style.opacity = '1';
      });
    }, 100);
  }, [theme]);

  // Ensure Ionicons web component is loaded
  useIoniconsEffect(() => {
    if (!window.customElements.get('ion-icon')) {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/ionicons@7.1.0/dist/ionicons/ionicons.esm.js';
      script.type = 'module';
      document.body.appendChild(script);
      const scriptNoModule = document.createElement('script');
      scriptNoModule.src = 'https://unpkg.com/ionicons@7.1.0/dist/ionicons/ionicons.js';
      scriptNoModule.noModule = true;
      document.body.appendChild(scriptNoModule);
  }
  }, []);

  // If module metadata is missing (undefined) after login, refresh once so
  // module-gated sidebar items render. Empty arrays mean the API returned none — do not retry.
  useEffect(() => {
    let cancelled = false;
    const roleSource = user?.roles ?? user?.roleName ?? null;
    const hasRoles = Array.isArray(roleSource) ? roleSource.length > 0 : Boolean(roleSource);
    const moduleCodesSource = user?.activeModuleCodes;
    const activeModulesSource = user?.activeModules;
    const modulesAlreadyResolved = Array.isArray(activeModulesSource) || Array.isArray(moduleCodesSource);
    const hasModuleCodes = Array.isArray(moduleCodesSource) && moduleCodesSource.length > 0;
    const hasActiveModules = Array.isArray(activeModulesSource) && activeModulesSource.length > 0;
    const hasAnyModuleData = hasModuleCodes || hasActiveModules;

    const userId = user?.id ?? user?.email ?? null;
    const refreshKey = userId != null ? String(userId) : '__unknown_user__';

    // If module data exists now, clear the attempt marker so future logins/impersonations can refresh as needed.
    if (hasAnyModuleData) {
      moduleRefreshAttemptedRef.current.delete(refreshKey);
    }

    if (!user || !hasRoles || hasAnyModuleData || modulesAlreadyResolved) return;
    if (moduleRefreshAttemptedRef.current.has(refreshKey)) return;
    moduleRefreshAttemptedRef.current.add(refreshKey);

    (async () => {
      try {
        await refreshUserPermissions();
      } catch (err) {
        if (!cancelled) {
          console.error('Layout - Failed to refresh user permissions for sidebar modules:', err);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, refreshUserPermissions]);

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const handleImpersonateUser = async (targetUserId) => {
    setImpersonationError('');
    const result = await impersonateUser(targetUserId);
    if (!result.success) {
      setImpersonationError(result.message);
    }
  };

  const handleStopImpersonation = async () => {
    setImpersonationError('');
    const result = await stopImpersonation();
    if (!result.success) {
      setImpersonationError(result.message);
    }
  };

  const menuItems = useMemo(() => filterNavigationMenu(menuStructure, user), [user]);
  const menuByPath = useMemo(() => {
    const map = {};
    const walk = (items) => {
      (items || []).forEach((item) => {
        if (item?.path && item.type !== 'section') map[item.path] = item;
        if (item?.children) walk(item.children);
      });
    };
    walk(menuItems);
    return map;
  }, [menuItems]);
  const isHomeMenuItem = (item) => item?.path === '/' || item?.id === 'home';

  const visibleFavorites = favoritePages
    .map((page) => menuByPath[page.path])
    .filter((item) => item && !isHomeMenuItem(item));
  const searchQuery = menuSearch.trim().toLowerCase();
  const isSearching = searchQuery.length > 0;
  const searchResults = useMemo(
    () => (isSearching ? collectMatchingMenuItems(menuItems, searchQuery) : []),
    [isSearching, menuItems, searchQuery]
  );
  useEffect(() => {
    rememberLastListPage(location.pathname);
  }, [location.pathname]);

  const dailyJumpItems = useMemo(
    () => ['home', 'calendar', 'todo-list']
      .map((id) => findMenuItemById(menuItems, id))
      .filter(Boolean),
    [menuItems]
  );

  // Debug logging for menu items and user permissions
  useEffect(() => {
    if (menuItems.some(item => item.children)) {
      menuItems.forEach(item => {
        if (item.children) {
        }
      });
    }
  }, [user, menuItems]);

  useEffect(() => {
    const findAncestorSections = (items, targetPath, ancestors = []) => {
      for (const item of items) {
        if (item.type === 'item' && item.path === targetPath) return ancestors;
        if (item.type === 'section' && item.children) {
          const match = findAncestorSections(item.children, targetPath, [...ancestors, item.id]);
          if (match) return match;
        }
      }
      return null;
    };
    const ancestors = findAncestorSections(menuItems, location.pathname);
    if (ancestors?.length) {
      setExpandedSections((current) => new Set([...current, ...ancestors]));
    }
  }, [location.pathname, menuItems]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;
      const tag = event.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || event.target?.isContentEditable) return;
      event.preventDefault();
      setSidebarOpen(true);
      writeSidebarOpenPreference(true);
      window.setTimeout(() => searchInputRef.current?.focus(), 50);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const handleMenuItemClick = (path) => {
    navigate(path);
    setMenuSearch('');
    if (isMobile || isTablet) {
      setSidebarOpen(false);
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  const getActiveMenuItem = () => {
    // Flatten the menu structure to find the active item
    const flattenMenu = (items) => {
      let flat = [];
      items.forEach(item => {
        if (item.type === 'section' && item.children) {
          flat = flat.concat(flattenMenu(item.children));
        } else {
          flat.push(item);
        }
      });
      return flat;
    };
    
    const flatMenu = flattenMenu(menuItems);
    if (location.pathname === '/bug-reports' || location.pathname.startsWith('/bug-reports/')) {
      return flatMenu.find((item) => item.path === '/bug-reports') || { label: 'Bug Reports', path: '/bug-reports' };
    }
    return flatMenu.find(item => item.path === location.pathname) || { label: 'Home', path: '/' };
  };

  const toggleSidebar = () => {
    setSidebarOpen((open) => {
      const next = !open;
      writeSidebarOpenPreference(next);
      return next;
    });
  };

  const toggleFavoritePage = (item, event) => {
    event.stopPropagation();
    if (!item?.path || isHomeMenuItem(item)) return;
    setFavoritePages((current) => {
      const exists = current.some((page) => page.path === item.path);
      const next = exists
        ? current.filter((page) => page.path !== item.path)
        : [...current, { path: item.path, label: item.label, icon: item.icon, id: item.id }];
      writeFavoritePages(next);
      return next;
    });
  };

  const markSidebarTouchMove = () => {
    lastSidebarTouchMoveAtRef.current = Date.now();
  };

  const handleMobileOverlayClick = () => {
    const msSinceSidebarScroll = Date.now() - lastSidebarTouchMoveAtRef.current;
    // Ignore accidental backdrop taps fired immediately after a scroll gesture.
    if (msSinceSidebarScroll < 325) {
      return;
    }
    setSidebarOpen(false);
  };

  // Map ion-icon names to Material-UI icons (or MDI icons via createMdiMenuIcon)
  const getIconComponent = (iconName) => {
    const iconMap = {
      'sprinkler-variant': SprinklerVariantIcon,
      'home-outline': Home,
      'bar-chart-outline': BarChart,
      'list-outline': List,
      'calendar-outline': CalendarToday,
      'checkbox-outline': Checklist,
      'document-text-outline': Description,
      'document-outline': Description,
      'checkmark-circle-outline': CheckCircle,
      'search-outline': Search,
      'flask-outline': Science,
      'settings-outline': Settings,
      'build-outline': Build,
      'library-outline': LibraryBooks,
      'business-outline': Business,
      'person-outline': Person,
      'log-out-outline': Logout,
      'chevron-up-outline': ExpandLess,
      'chevron-down-outline': ExpandMore,
      'stats-chart-outline': TrendingUp,
      'map-outline': Map,
      'water-outline': Water,
      'shield-checkmark-outline': Shield,
      'notifications-outline': Notifications,
      'bug-outline': BugReport,
      'people-outline': People,
      'grid-outline': GridOn,
      'construct-outline': Construction,
      'leaf-outline': Nature,
      'analytics-outline': Analytics,
      'trash-outline': Delete,
      'receipt-outline': Receipt,
      'card-outline': CreditCard,
      'car-outline': DirectionsCar,
      'location-outline': LocationOn,
      'folder-outline': Folder,
      'speedometer-outline': Speed,
      'color-palette-outline': ColorLens,
      'refresh-outline': Refresh,
      'mail-outline': Mail,
      'time-outline': Schedule,
      'sunny-outline': Settings, // Use Settings as fallback for theme toggle
      'moon-outline': Settings,  // Use Settings as fallback for theme toggle
    };
    
    return iconMap[iconName] || Description; // Default to Description if not found
  };

  const renderFavoriteButton = (item) => {
    if (!item?.path || isSidebarCollapsed || isHomeMenuItem(item)) return null;
    const favorited = favoritePages.some((page) => page.path === item.path);
    return (
      <CursorTooltip title={favorited ? 'Remove favorite' : 'Favorite this page'}>
        <button
          type="button"
          className="sidebar-favorite-btn"
          aria-label={favorited ? `Remove ${item.label} from favorites` : `Favorite ${item.label}`}
          onClick={(event) => toggleFavoritePage(item, event)}
        >
          {favorited ? <Star style={{ fontSize: 16 }} /> : <StarBorder style={{ fontSize: 16 }} />}
        </button>
      </CursorTooltip>
    );
  };

  const renderSidebarChild = (node, depth = 1) => {
    const isNodeSection = node.type === 'section';
    const isNodeActive = !isNodeSection && location.pathname === node.path;
    const isNodeExpanded = expandedSections.has(node.id);
    const IconComponent = getIconComponent(node.icon);

    return (
      <div key={node.id}>
        <div
          onClick={() => {
            if (isNodeSection) toggleSection(node.id);
            else handleMenuItemClick(node.path);
          }}
          style={{
            padding: isMobile ? '12px 16px' : '8px 16px',
            margin: isMobile ? '4px 8px' : '2px 4px',
            borderRadius: '6px',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            background: isNodeActive ? 'rgba(61, 99, 141, 0.12)' : 'transparent',
            border: isNodeActive ? '1px solid var(--color-accent)' : '1px solid transparent',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: isNodeActive ? 'var(--color-accent)' : 'var(--color-text-secondary)',
            minHeight: isMobile ? '36px' : 'auto',
          }}
          onMouseEnter={(event) => {
            if (!isMobile) {
              event.currentTarget.style.background = isNodeActive
                ? 'rgba(61, 99, 141, 0.18)'
                : 'rgba(255,255,255,0.03)';
              event.currentTarget.style.transform = 'translateX(2px)';
            }
          }}
          onMouseLeave={(event) => {
            if (!isMobile) {
              event.currentTarget.style.background = isNodeActive
                ? 'rgba(61, 99, 141, 0.12)'
                : 'transparent';
              event.currentTarget.style.transform = 'translateX(0)';
            }
          }}
        >
          <span style={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            minWidth: isMobile ? '20px' : '18px',
            justifyContent: 'center',
          }}>
            <IconComponent style={{ fontSize: isMobile ? '18px' : '16px', color: 'inherit' }} />
          </span>
          <span style={{
            fontSize: isMobile ? '14px' : '12px',
            fontWeight: isNodeActive || isNodeSection ? '600' : '500',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            textAlign: 'left',
            flex: 1,
          }}>
            {node.label}
          </span>
          {!isNodeSection && renderFavoriteButton(node)}
          {isNodeSection && (
            <span style={{ display: 'flex', alignItems: 'center' }}>
              {React.createElement(
                getIconComponent(isNodeExpanded ? 'chevron-up-outline' : 'chevron-down-outline'),
                { style: { fontSize: '14px' } }
              )}
            </span>
          )}
        </div>
        {isNodeSection && isNodeExpanded && node.children?.length > 0 && (
          <div style={{ marginLeft: isMobile ? '18px' : '14px', overflow: 'hidden' }}>
            {node.children.map((child) => renderSidebarChild(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const isDesktop = !isMobile && !isTablet;
  const isSidebarCollapsed = isDesktop && !sidebarOpen;
  const sidebarPermissionsLoading = Boolean(user) && (loading || !permissionsReady);

  const renderSidebarSearch = (sticky) => (
    <div
      className="sidebar-nav-search"
      style={{
        position: sticky ? 'sticky' : 'static',
        top: sticky ? 0 : 'auto',
        zIndex: sticky ? 2 : 'auto',
        padding: isMobile ? '10px 16px 12px' : '8px 12px 12px',
        background: 'var(--color-bg-alt)',
        borderBottom: '1px solid var(--color-border)',
        flexShrink: 0
      }}
    >
      {(isMobile || isTablet) && dailyJumpItems.length > 0 && (
        <div className="sidebar-jump-row" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
          {dailyJumpItems.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => handleMenuItemClick(item.path)}
              style={{
                border: location.pathname === item.path
                  ? '1px solid var(--color-accent)'
                  : '1px solid var(--color-border)',
                background: location.pathname === item.path
                  ? 'rgba(61, 99, 141, 0.15)'
                  : 'var(--color-bg)',
                color: location.pathname === item.path
                  ? 'var(--color-accent)'
                  : 'var(--color-text)',
                borderRadius: 999,
                padding: '6px 10px',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
      <div style={{ position: 'relative' }}>
        <Search
          style={{
            position: 'absolute',
            left: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: 20,
            width: 20,
            height: 20,
            color: 'var(--color-text-secondary)',
            pointerEvents: 'none'
          }}
        />
        <input
          ref={searchInputRef}
          className="sidebar-menu-search"
          type="search"
          value={menuSearch}
          onChange={(event) => setMenuSearch(event.target.value)}
          placeholder="Search pages"
          aria-label="Search pages"
        />
      </div>
    </div>
  );

  return (
    <PageTitleContext.Provider value={pageTitleContextValue}>
    <div
      className="app-shell"
      style={{
      minHeight: (isMobile || isTablet) ? 0 : '100vh',
      background: 'var(--color-bg)',
      position: 'relative',
      overflow: 'hidden',
      display: 'flex',
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box'
  }}>
      {/* Animated background elements */}
      <div style={{
        position: 'absolute',
        top: '-50%',
        left: '-50%',
        width: '200%',
        height: '200%',
        background: 'radial-gradient(circle, var(--color-accent)05 0%, transparent 70%)',
        animation: 'float 30s ease-in-out infinite',
      }} />
      <div style={{
        position: 'absolute',
        bottom: '-50%',
        right: '-50%',
        width: '200%',
        height: '200%',
        background: 'radial-gradient(circle, var(--color-accent2)05 0%, transparent 70%)',
        animation: 'float 35s ease-in-out infinite reverse',
      }} />
      
      {/* Mobile/Tablet Overlay */}
      {(isMobile || isTablet) && (
        <div 
          className={`mobile-overlay ${sidebarOpen ? 'open' : ''}`}
          onClick={handleMobileOverlayClick}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0,0,0,0.5)',
            zIndex: 999,
            opacity: sidebarOpen ? 1 : 0,
            visibility: sidebarOpen ? 'visible' : 'hidden',
            transition: 'all 0.3s ease',
          }}
        />
      )}
      
      {/* Sidebar */}
      <div 
        className={`sidebar ${sidebarOpen ? 'open' : ''} ${isSidebarCollapsed ? 'collapsed' : ''}`}
        style={{
          width: (isMobile || isTablet) ? '100%' : (sidebarOpen ? '280px' : '72px'),
          minWidth: (isMobile || isTablet) ? undefined : (sidebarOpen ? '280px' : '72px'),
          maxWidth: (isMobile || isTablet) ? undefined : (sidebarOpen ? '280px' : '72px'),
          flexShrink: 0,
          background: 'var(--color-bg-alt)',
          backdropFilter: 'blur(20px)',
          borderRight: '1px solid var(--color-border)',
          position: (isMobile || isTablet) ? 'fixed' : 'relative',
          top: (isMobile || isTablet) ? 0 : 'auto',
          left: (isMobile || isTablet) ? (sidebarOpen ? 0 : '-100%') : 'auto',
          height: (isMobile || isTablet) ? '100dvh' : 'auto',
          minHeight: (isMobile || isTablet) ? 0 : 'auto',
          maxHeight: (isMobile || isTablet) ? '100dvh' : 'none',
          boxSizing: 'border-box',
          zIndex: (isMobile || isTablet) ? 1000 : 2,
          transition: (isMobile || isTablet) ? 'left 0.3s ease' : 'width 0.3s ease',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
  }}
      >
        {/* Sidebar Header — height locked to --app-header-height with main .header */}
        <div
          className="sidebar-header"
          style={{
          padding: isSidebarCollapsed ? '0 8px' : (isMobile ? '0 16px' : '0 20px'),
          borderBottom: '1px solid var(--color-border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: isSidebarCollapsed ? 'center' : 'flex-start',
          gap: isSidebarCollapsed ? '0' : '15px',
          height: 'var(--app-header-height)',
          boxSizing: 'border-box',
          flexShrink: 0,
          overflow: 'hidden'
  }}>
          {!isSidebarCollapsed && (
            <img 
              src={theme === 'light' ? '/Blue Ox Version 2.jpg' : '/Blue Ox Version 4.jpg'}
              alt="Blue Ox Logo"
              style={{
                width: '38px',
                height: '38px',
                borderRadius: '50%',
                objectFit: 'cover',
                objectPosition: 'center',
                flexShrink: 0
              }}
            />
          )}
          {(sidebarOpen || isMobile || isTablet) && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <h2 style={{ 
                margin: 0, 
                fontSize: isMobile ? '18px' : '16px', 
                fontWeight: '700',
                color: 'var(--color-text)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
  }}>
                Blue Ox
              </h2>
              <p style={{ 
                margin: 0, 
                fontSize: isMobile ? '14px' : '12px', 
                color: 'var(--color-text-secondary)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
  }}>
                Compliance Portal
              </p>
            </div>
          )}
          {/* Desktop/Tablet Toggle Button (also the only header control when rail is collapsed) */}
          {!isMobile && (
            <button
              type="button"
              className="sidebar-collapse-btn"
              onClick={toggleSidebar}
              aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
              style={{
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid var(--color-border)',
                borderRadius: '6px',
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'var(--color-text)',
                transition: 'all 0.3s ease',
                flexShrink: 0,
                padding: 0,
                lineHeight: 1,
  }}
            >
              {sidebarOpen ? <ChevronLeft /> : <ChevronRight />}
            </button>
          )}
          
          {/* Mobile Close Button */}
          {isMobile && sidebarOpen && (
            <button
              type="button"
              className="sidebar-close-btn"
              onClick={() => setSidebarOpen(false)}
              style={{
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid var(--color-border)',
                borderRadius: '6px',
                width: '44px',
                height: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'var(--color-text)',
                fontSize: '18px',
                transition: 'all 0.3s ease',
                flexShrink: 0,
                minWidth: '44px',
                minHeight: '44px',
                overflow: 'visible'
  }}
              aria-label="Close sidebar"
            >
              <ChevronLeft />
            </button>
          )}
        </div>

        {/* Mobile Controls - Vertical Layout */}
        <div style={{ 
          display: isMobile ? 'flex' : 'none', 
          flexDirection: 'column',
          padding: '12px',
          borderBottom: '1px solid var(--color-border)',
          background: 'var(--color-bg-alt)',
          gap: '12px',
          flexShrink: 0
  }}>
          {/* Impersonation Alert */}
          {user?.isImpersonated && (
            <Alert 
              severity="warning" 
              sx={{ 
                py: 0.5, 
                px: 1.5, 
                '& .MuiAlert-message': { py: 0.5, fontSize: '11px' },
                '& .MuiAlert-action': { pl: 0, pr: 0 }
              }}
              action={
                <Button
                  size="small"
                  color="inherit"
                  onClick={handleStopImpersonation}
                  startIcon={<ExitToApp />}
                  sx={{ fontSize: '10px', minWidth: 'auto', px: 1 }}
                >
                  Exit
                </Button>
              }
            >
              Impersonating: {user.firstName} {user.lastName}
            </Alert>
          )}
          
          {/* Welcome Section */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            padding: '8px 12px',
            background: 'rgba(61, 99, 141, 0.08)',
            borderRadius: '12px',
            border: '1px solid var(--color-accent)'
  }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '6px',
                height: '6px',
                background: 'var(--color-success)',
                borderRadius: '50%',
                animation: 'pulse 2s infinite',
                flexShrink: 0
              }}></div>
              <span style={{
                fontSize: '12px',
                color: 'var(--color-text-secondary)',
                flex: 1,
                textAlign: 'left'
              }}>
                Welcome, {user?.firstName || 'User'}!
              </span>
            </div>

            <label style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '8px',
              cursor: 'pointer'
            }}>
              <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-text)' }}>
                {theme === 'light' ? 'Light mode' : 'Dark mode'}
              </span>
              <span style={{
                position: 'relative',
                display: 'inline-block',
                width: '60px',
                height: '28px',
                flexShrink: 0
              }}>
                <input
                  type="checkbox"
                  checked={theme === 'light'}
                  onChange={toggleTheme}
                  aria-label={theme === 'light' ? 'Light mode' : 'Dark mode'}
                  style={{ opacity: 0, width: 0, height: 0 }}
                />
                <span style={{
                  position: 'absolute',
                  cursor: 'pointer',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: theme === 'light'
                    ? 'linear-gradient(135deg, #fff, #e0e0e0)'
                    : 'linear-gradient(135deg, var(--color-accent), var(--color-accent2))',
                  borderRadius: '28px',
                  transition: 'background 0.4s',
                  border: theme === 'light' ? '1px solid var(--color-border)' : 'none',
                }}></span>
                <span style={{
                  position: 'absolute',
                  height: '20px',
                  width: '20px',
                  left: theme === 'light' ? '32px' : '4px',
                  bottom: '4px',
                  backgroundColor: theme === 'light' ? '#fff' : 'var(--color-text)',
                  borderRadius: '50%',
                  transition: 'left 0.4s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                  border: theme === 'light' ? '1px solid var(--color-border)' : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  color: theme === 'light' ? 'var(--color-accent)' : 'var(--color-accent2)',
                  padding: 0,
                }}>
                  <ion-icon
                    className="sidebar-toggle-icon"
                    name={theme === 'light' ? 'sunny-outline' : 'moon-outline'}
                    style={{
                      fontSize: '14px',
                      color: theme === 'light' ? 'var(--color-accent)' : 'var(--color-accent2)',
                      '--color': theme === 'light' ? 'var(--color-accent)' : 'var(--color-accent2)',
                      display: 'inline-block',
                      visibility: 'visible',
                      opacity: '1'
                    }}
                  ></ion-icon>
                </span>
              </span>
            </label>
            <CompactDatagridToggle size="sidebar" label="Compact rows" />
          </div>
          
          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '6px', flexDirection: 'column' }}>
            {/* Profile Button */}
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate('/profile')}
              startIcon={<ion-icon name="person-outline" style={{ fontSize: '14px' }}></ion-icon>}
              sx={{
                fontSize: '11px',
                borderColor: 'var(--color-accent)',
                color: 'var(--color-accent)',
                py: 0.5,
                px: 1.5,
                minHeight: '32px',
                '&:hover': {
                  borderColor: 'var(--color-accent2)',
                  color: 'var(--color-accent2)'
  }
              }}
            >
              Profile
            </Button>
            
            {/* Impersonation Button for Global Admins */}
            {!user?.isImpersonated && (
              (user?.roles && Array.isArray(user.roles) && user.roles.some(role => (role.roleName === 'globaladmin' || role === 'globaladmin'))) ||
              user?.roleName === 'globaladmin' ||
              user?.role === 'globaladmin'
            ) && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => setShowImpersonationModal(true)}
                startIcon={<AdminPanelSettings />}
                sx={{
                  fontSize: '11px',
                  borderColor: 'var(--color-accent)',
                  color: 'var(--color-accent)',
                  py: 0.5,
                  px: 1.5,
                  minHeight: '32px',
                  '&:hover': {
                    borderColor: 'var(--color-accent2)',
                    color: 'var(--color-accent2)'
  }
                }}
              >
                Impersonate
              </Button>
            )}
          </div>
        </div>

        {isMobile && renderSidebarSearch(false)}

        {/* Navigation Menu */}
        <nav
          onTouchMove={markSidebarTouchMove}
          style={{
            flex: 1,
            minHeight: 0,
            padding: isMobile ? '0 0 16px' : '8px 0 20px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {isSidebarCollapsed ? (
            <button
              type="button"
              className="sidebar-search-expand-btn"
              onClick={() => {
                setSidebarOpen(true);
                writeSidebarOpenPreference(true);
                window.setTimeout(() => searchInputRef.current?.focus(), 80);
              }}
              aria-label="Search pages"
              style={{
                margin: '4px 8px 8px',
                width: 'calc(100% - 16px)',
                height: '40px',
                borderRadius: '8px',
                border: '1px solid var(--color-border)',
                background: 'var(--color-bg)',
                color: 'var(--color-text)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Search style={{ fontSize: 20 }} />
            </button>
          ) : !isMobile ? renderSidebarSearch(true) : null}
          {sidebarPermissionsLoading ? (
            <div
              role="status"
              aria-busy="true"
              aria-label="Loading navigation"
              style={{
                padding: isMobile ? '8px 12px' : '4px 8px',
                display: 'flex',
                flexDirection: 'column',
                gap: isMobile ? '10px' : '8px'
              }}
            >
              {[0.78, 0.55, 0.68].map((widthFrac, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: isMobile ? '16px 20px' : (sidebarOpen ? '12px 20px' : '12px'),
                    margin: isMobile ? '8px 16px' : '4px 8px',
                    borderRadius: '8px',
                    justifyContent: (sidebarOpen || isMobile || isTablet) ? 'flex-start' : 'center'
                  }}
                >
                  <Skeleton
                    variant="circular"
                    animation="wave"
                    width={isMobile ? 24 : 20}
                    height={isMobile ? 24 : 20}
                    sx={{
                      bgcolor: 'action.hover',
                      flexShrink: 0
                    }}
                  />
                  {(sidebarOpen || isMobile || isTablet) && (
                    <Skeleton
                      variant="rounded"
                      animation="wave"
                      height={isMobile ? 18 : 14}
                      sx={{
                        flex: 1,
                        maxWidth: `${Math.round(widthFrac * 100)}%`,
                        bgcolor: 'action.hover'
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          ) : menuItems.length === 0 ? (
            <div
              style={{
                padding: isMobile ? '16px 20px' : '16px 12px',
                color: 'var(--color-text-secondary)',
                fontSize: 13,
                lineHeight: 1.45
              }}
            >
              No modules assigned — contact your administrator.
            </div>
          ) : isSearching ? (
            searchResults.length === 0 ? (
              <div
                style={{
                  padding: isMobile ? '16px 20px' : '16px 12px',
                  color: 'var(--color-text-secondary)',
                  fontSize: 13
                }}
              >
                No pages match "{menuSearch.trim()}".
              </div>
            ) : (
              searchResults.map((item) => {
                const isActive = location.pathname === item.path;
                const IconComponent = getIconComponent(item.icon);
                return (
                  <div
                    key={`${item.id}-${item.path}`}
                    onClick={() => handleMenuItemClick(item.path)}
                    style={{
                      padding: isMobile ? '16px 20px' : (sidebarOpen ? '12px 20px' : '12px'),
                      margin: isMobile ? '8px 16px' : '4px 8px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      background: isActive ? 'rgba(61, 99, 141, 0.15)' : 'transparent',
                      border: isActive ? '1px solid var(--color-accent)' : '1px solid transparent',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      color: isActive ? 'var(--color-accent)' : 'var(--color-text-secondary)'
                    }}
                  >
                    <IconComponent style={{ fontSize: isMobile ? '22px' : '20px', color: 'inherit', flexShrink: 0 }} />
                    <span style={{ fontSize: isMobile ? '16px' : '14px', fontWeight: isActive ? 600 : 500, textAlign: 'left' }}>
                      {item.label}
                      {item.ancestorLabel ? (
                        <span
                          style={{
                            display: 'block',
                            fontSize: 11,
                            color: 'var(--color-text-secondary)',
                            fontWeight: 400
                          }}
                        >
                          {item.ancestorLabel}
                        </span>
                      ) : null}
                    </span>
                  </div>
                );
              })
            )
          ) : menuItems.map((item) => {
            // Only mark as active if it's an actual menu item (not a section) and the path matches
            const isActive = item.type === 'item' && location.pathname === item.path;
            const isSection = item.type === 'section';
            const hasChildren = item.children && item.children.length > 0;
            const isExpanded = expandedSections.has(item.id);
            
            return (
              <div key={item.id}>
                {/* Section Header or Menu Item */}
                <div
                  onClick={() => {
                    if (isSection) {
                      toggleSection(item.id);
                    } else {
                      handleMenuItemClick(item.path);
                    }
                  }}
                  style={{
                    padding: isMobile ? '16px 20px' : (sidebarOpen ? '12px 20px' : '12px'),
                    margin: isMobile ? '8px 16px' : '4px 8px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    background: isActive 
                      ? 'rgba(61, 99, 141, 0.15)' 
                      : 'transparent',
                    border: isActive 
                      ? '1px solid var(--color-accent)' 
                      : '1px solid transparent',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    justifyContent: (sidebarOpen || isMobile || isTablet) ? 'flex-start' : 'center',
                    color: isActive ? 'var(--color-accent)' : 'var(--color-text-secondary)',
                    minHeight: isMobile ? '44px' : 'auto',
                  }}
                  onMouseEnter={(e) => {
                    if (!isMobile) {
                      e.target.style.background = isActive 
                        ? 'rgba(61, 99, 141, 0.2)' 
                        : 'rgba(255,255,255,0.05)';
                      e.target.style.transform = 'translateX(4px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isMobile) {
                      e.target.style.background = isActive 
                        ? 'rgba(61, 99, 141, 0.15)' 
                        : 'transparent';
                      e.target.style.transform = 'translateX(0)';
                    }
                  }}
                >
                  <span style={{ 
                    fontSize: isMobile ? '20px' : '18px', 
                    flexShrink: 0, 
                    display: 'flex', 
                    alignItems: 'center',
                    minWidth: isMobile ? '24px' : '20px',
                    justifyContent: 'center'
                  }}>
                    {(() => {
                      const IconComponent = getIconComponent(item.icon);
                      return <IconComponent style={{ 
                        fontSize: isMobile ? '22px' : '20px', 
                        color: 'inherit'
                      }} />;
                    })()}
                  </span>
                  {(sidebarOpen || isMobile || isTablet) && (
                  <span style={{ 
                    fontSize: isMobile ? '16px' : '14px', 
                    fontWeight: isActive ? '600' : '500',
                    whiteSpace: 'break-spaces',
                    overflow: 'hidden',
                    textOverflow: 'word-wrap',
                    flex: 1,
                    textAlign: 'left'
  }}>
                    {item.label}
                  </span>
                  )}
                  {!isSection && renderFavoriteButton(item)}
                  {isSection && (sidebarOpen || isMobile || isTablet) && (
                    <span style={{ fontSize: '12px', color: 'inherit', transition: 'transform 0.3s ease' }}>
                      {(() => {
                        const IconComponent = getIconComponent(isExpanded ? "chevron-up-outline" : "chevron-down-outline");
                        return <IconComponent style={{ 
                          fontSize: '16px',
                          transform: isExpanded ? 'rotate(0deg)' : 'rotate(0deg)'
                        }} />;
                      })()}
                    </span>
                  )}
                </div>
                
                {/* Section Children */}
                {isSection && hasChildren && isExpanded && (sidebarOpen || isMobile || isTablet) && (
                  <div style={{ 
                    marginLeft: isMobile ? '32px' : '20px',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease'
  }}>
                    {item.children.map((child) => renderSidebarChild(child))}
                  </div>
                )}
                {isHomeMenuItem(item) && !isSearching && visibleFavorites.length > 0 && (
                  <div style={{ marginBottom: 8 }}>
                    {(sidebarOpen || isMobile || isTablet) && (
                      <div style={{
                        padding: '4px 20px 6px',
                        color: 'var(--color-text-secondary)',
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: '0.04em',
                        textTransform: 'uppercase'
                      }}
                      >
                        Favorites
                      </div>
                    )}
                    {visibleFavorites.map((favorite) => {
                      const favoriteActive = location.pathname === favorite.path;
                      const FavoriteIcon = getIconComponent(favorite.icon);
                      return (
                        <div
                          key={`favorite-${favorite.id || favorite.path}`}
                          onClick={() => handleMenuItemClick(favorite.path)}
                          style={{
                            padding: isMobile ? '12px 16px' : (sidebarOpen ? '8px 16px' : '10px'),
                            margin: isMobile ? '4px 16px' : '2px 8px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            background: favoriteActive ? 'rgba(61, 99, 141, 0.15)' : 'transparent',
                            border: favoriteActive ? '1px solid var(--color-accent)' : '1px solid transparent',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            justifyContent: (sidebarOpen || isMobile || isTablet) ? 'flex-start' : 'center',
                            color: favoriteActive ? 'var(--color-accent)' : 'var(--color-text-secondary)'
                          }}
                        >
                          <FavoriteIcon style={{ fontSize: isMobile ? 20 : 18, color: 'inherit', flexShrink: 0 }} />
                          {(sidebarOpen || isMobile || isTablet) && (
                            <span style={{ fontSize: isMobile ? 15 : 13, fontWeight: 600, flex: 1, textAlign: 'left' }}>
                              {favorite.label}
                            </span>
                          )}
                          {renderFavoriteButton(favorite)}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Sidebar Footer */}
        <div style={{
          padding: isMobile ? '16px' : (isSidebarCollapsed ? '12px 8px' : '20px'),
          borderTop: '1px solid var(--color-border)',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          flexShrink: 0,
          overflow: 'hidden'
  }}>
          
          
          <button 
            onClick={handleLogout}
            style={{
              padding: isMobile ? '12px 16px' : (sidebarOpen ? '10px 16px' : '10px'),
              background: 'rgba(244, 67, 54, 0.1)',
              color: 'var(--color-error)',
              border: '1px solid var(--color-error)',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: isMobile ? '14px' : '12px',
              fontWeight: '600',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              justifyContent: isMobile ? 'flex-start' : (sidebarOpen ? 'flex-start' : 'center'),
              minHeight: isMobile ? '44px' : 'auto',
            }}
            onMouseEnter={(e) => {
              if (!isMobile) {
                e.target.style.background = 'rgba(244, 67, 54, 0.2)';
                e.target.style.transform = 'translateY(-1px)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isMobile) {
                e.target.style.background = 'rgba(244, 67, 54, 0.1)';
                e.target.style.transform = 'translateY(0)';
              }
            }}
          >
            <span style={{ fontSize: isMobile ? '16px' : '14px', display: 'flex', alignItems: 'center' }}>
              {(() => {
                const IconComponent = getIconComponent("log-out-outline");
                return <IconComponent style={{ 
                  fontSize: isMobile ? '18px' : '16px', 
                  color: 'inherit'
                }} />;
              })()}
            </span>
            {(sidebarOpen || isMobile || isTablet) && <span>Logout</span>}
          </button>
        </div>
      </div>

      {/* Main Content Area — minWidth:0 so flex can shrink beside the sidebar */}
      <div
        className="layout-main"
        style={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column',
        marginLeft: 0,
        minWidth: 0,
        maxWidth: '100%',
        overflow: 'hidden',
        width: 'auto'
  }}>
        {/* Top Header */}
        <div 
          className={`header${user?.isImpersonated ? ' header--impersonating' : ''}`}
          style={{
            background: 'var(--color-bg-alt)',
            backdropFilter: 'blur(20px)',
            color: 'var(--color-text)',
            padding: isMobile ? '0 12px' : (isTablet || isSidebarCollapsed ? '0 16px' : '0 24px'),
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '12px',
            borderBottom: '1px solid var(--color-border)',
            position: 'relative',
            top: 'auto',
            zIndex: isMobile ? 100 : 1,
            height: 'var(--app-header-height)',
            boxSizing: 'border-box',
            flexShrink: 0,
            minWidth: 0,
            maxWidth: '100%',
            overflow: 'visible'
  }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: (isMobile || isTablet || !sidebarOpen) ? '12px' : '20px',
            minWidth: 0,
            flex: '1 1 auto',
            overflow: 'hidden'
          }}>
            {/* Overlay closed or docked collapsed: always offer a reopen control */}
            {((isMobile || isTablet) || !sidebarOpen) && (
              <button
                type="button"
                className="sidebar-nav-toggle-btn"
                onClick={toggleSidebar}
                aria-label="Toggle navigation menu"
                aria-expanded={sidebarOpen}
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '6px',
                  width: '44px',
                  height: '44px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: 'var(--color-text)',
                  fontSize: '18px',
                  transition: 'all 0.3s ease',
                  flexShrink: 0,
                  minWidth: '44px',
                  minHeight: '44px'
  }}
              >
                <Menu />
              </button>
            )}
            <h1 style={{ 
              margin: 0, 
              fontSize: isMobile ? '20px' : '24px', 
              fontWeight: '700',
              color: 'var(--color-accent)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0
  }}>
              {pageTitle || getActiveMenuItem().label}
            </h1>
          </div>
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: isTablet || isSidebarCollapsed ? '8px' : '16px',
            flexShrink: 0,
            minWidth: 0,
            overflow: 'visible'
          }}>
            <BugReportButton />
            <NotificationBell />
            <div style={{
            display: isMobile ? 'none' : 'flex',
            alignItems: 'center',
            gap: isTablet || isSidebarCollapsed ? '8px' : '16px',
            flexShrink: 1,
            minWidth: 0,
            overflow: 'hidden'
          }}>
            {/* Impersonation UI — single line so the stop control cannot wrap out of the 72px header */}
            {user?.isImpersonated && (
              <div className="header-impersonation" role="status">
                <ion-icon className="header-impersonation__icon" name="warning-outline" aria-hidden="true" />
                <CursorTooltip title={`Impersonating ${[user.firstName, user.lastName].filter(Boolean).join(' ')}`}>
                  <span className="header-impersonation__label">
                    Impersonating {[user.firstName, user.lastName].filter(Boolean).join(' ')}
                  </span>
                </CursorTooltip>
                <CursorTooltip title="Stop impersonating">
                  <button
                    type="button"
                    className="header-impersonation__stop"
                    onClick={handleStopImpersonation}
                  >
                    Stop impersonating
                  </button>
                </CursorTooltip>
              </div>
            )}
            
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: isMobile ? '6px 12px' : '8px 16px',
              background: 'rgba(61, 99, 141, 0.08)',
              borderRadius: '20px',
              border: '1px solid var(--color-accent)',
              flexShrink: 0
  }}>
              <div style={{
                width: '8px',
                height: '8px',
                background: 'var(--color-success)',
                borderRadius: '50%',
                animation: 'pulse 2s infinite'
  }}></div>
              <span
                className="header-welcome-text"
                style={{ 
                fontSize: isMobile ? '12px' : '14px', 
                color: 'var(--color-text-secondary)',
                display: (isMobile || isTablet || isSidebarCollapsed) ? 'none' : 'block',
                whiteSpace: 'nowrap'
  }}>
                Welcome, {user?.firstName || 'User'}!
              </span>
              {(isMobile || isTablet || isSidebarCollapsed) && (
                <span style={{ 
                  fontSize: '12px', 
                  color: 'var(--color-text-secondary)',
                  whiteSpace: 'nowrap'
  }}>
                  {user?.firstName || 'User'}
                </span>
              )}
              {/* Theme Toggle Switch */}
              <label style={{
                position: 'relative',
                display: 'inline-block',
                width: isMobile ? '48px' : '64px',
                height: isMobile ? '26px' : '36px',
                marginLeft: isMobile || isTablet || isSidebarCollapsed ? '4px' : '12px',
                flexShrink: 0
              }}>
                <input
                  type="checkbox"
                  checked={theme === 'light'}
                  onChange={toggleTheme}
                  style={{ opacity: 0, width: 0, height: 0 }}
                />
                <span style={{
                  position: 'absolute',
                  cursor: 'pointer',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: theme === 'light'
                    ? 'linear-gradient(135deg, #fff, #e0e0e0)'
                    : 'linear-gradient(135deg, var(--color-accent), var(--color-accent2))',
                  borderRadius: isMobile ? '26px' : '36px',
                  transition: 'background 0.4s',
                  border: theme === 'light' ? '1px solid var(--color-border)' : 'none',
                }}></span>
                <span style={{
                  position: 'absolute',
                  height: isMobile ? '20px' : '30px',
                  width: isMobile ? '20px' : '30px',
                  left: theme === 'light' ? (isMobile ? '24px' : '34px') : (isMobile ? '4px' : '6px'),
                  bottom: isMobile ? '3px' : '3px',
                  backgroundColor: theme === 'light' ? '#fff' : 'var(--color-text)',
                  borderRadius: '50%',
                  transition: 'left 0.4s',
                  boxShadow: theme === 'light'
                    ? '0 2px 6px rgba(0,0,0,0.10)'
                    : '0 2px 6px var(--color-accent2, #00bcd4, 0.15)',
                  border: theme === 'light' ? '1px solid var(--color-border)' : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: isMobile ? '18px' : '22px',
                  color: 'linear-gradient(135deg, var(--color-accent), var(--color-accent2))',
                  padding: 0,
                }}>
                  <ion-icon 
                    name={theme === 'light' ? 'sunny-outline' : 'moon-outline'} 
                    style={{ 
                      fontSize: isMobile ? '18px' : '22px',
                      color: theme === 'light' ? 'var(--color-accent)' : 'var(--color-accent2)',
                      '--color': theme === 'light' ? 'var(--color-accent)' : 'var(--color-accent2)',
                      display: 'inline-block',
                      visibility: 'visible',
                      opacity: '1'
                    }}
                  ></ion-icon>
                </span>
              </label>
              <CompactDatagridToggle size="header" isMobile={isMobile} />
              
              {/* Profile Button */}
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate('/profile')}
                startIcon={<ion-icon name="person-outline" style={{ fontSize: isMobile ? '16px' : '18px' }}></ion-icon>}
                sx={{
                  ml: 0,
                  flexShrink: 0,
                  fontSize: isMobile ? '11px' : '12px',
                  borderColor: 'var(--color-accent)',
                  color: 'var(--color-accent)',
                  '&:hover': {
                    borderColor: 'var(--color-accent2)',
                    color: 'var(--color-accent2)'
  }
                }}
              >
                Profile
              </Button>
              
              {/* Impersonation Button for Global Admins */}
              {!user?.isImpersonated && (
                (user?.roles && Array.isArray(user.roles) && user.roles.some(role => (role.roleName === 'globaladmin' || role === 'globaladmin'))) ||
                user?.roleName === 'globaladmin' ||
                user?.role === 'globaladmin'
              ) && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setShowImpersonationModal(true)}
                  startIcon={<AdminPanelSettings />}
                  sx={{
                    ml: 0,
                    flexShrink: 0,
                    fontSize: isMobile ? '11px' : '12px',
                    borderColor: 'var(--color-accent)',
                    color: 'var(--color-accent)',
                    '&:hover': {
                      borderColor: 'var(--color-accent2)',
                      color: 'var(--color-accent2)'
  }
                  }}
                >
                  {isTablet || isSidebarCollapsed ? 'Impersonate' : 'Impersonate User'}
                </Button>
              )}
            </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div 
          className="main-content"
          style={{ 
            flex: 1,
            padding: isMobile ? '8px' : (isTablet || isSidebarCollapsed ? '16px' : '24px'), 
            position: 'relative',
            zIndex: 1,
            overflowX: 'auto',
            overflowY: 'auto',
            minWidth: 0,
            minHeight: (isMobile || isTablet) ? 0 : 'auto',
            maxWidth: '100%',
            boxSizing: 'border-box'
  }}
        >
          {children}
        </div>
      </div>

      {/* Impersonation Modal */}
      <UserImpersonationModal
        open={showImpersonationModal}
        onClose={() => setShowImpersonationModal(false)}
        onImpersonate={handleImpersonateUser}
      />

      {/* Impersonation Error Alert */}
      {impersonationError && (
        <Alert 
          severity="error" 
          sx={{ 
            position: 'fixed',
            top: 20,
            right: 20,
            zIndex: 9999,
            maxWidth: 400
  }}
          onClose={() => setImpersonationError('')}
        >
          {impersonationError}
        </Alert>
      )}
    </div>
    </PageTitleContext.Provider>
  );
};

export default Layout; 




